import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, PenTool, Clock, Send, ThumbsUp, Bookmark,
  MessageCircle, Trophy, Star, Crown, Flame, CheckCircle,
  Loader2, ChevronRight, Award, Users, TrendingUp, Eye
} from "lucide-react";

const AnswerArenaPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [arena, setArena] = useState(null);
  const [selectedQ, setSelectedQ] = useState(0);
  const [answer, setAnswer] = useState("");
  const [evaluation, setEvaluation] = useState(null);

  const [tab, setTab] = useState("write");
  const [submissions, setSubmissions] = useState([]);
  const [bestAnswers, setBestAnswers] = useState(null);
  const [viewingSub, setViewingSub] = useState(null);
  const [sortBy, setSortBy] = useState("score");
  const [bestPeriod, setBestPeriod] = useState("today");
  const [communityQ, setCommunityQ] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  useEffect(() => { loadArena(); }, []);

  const loadArena = async () => {
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/arena/today`, { headers });
      const data = await res.json();
      setArena(data);
    } catch { toast({ variant: "destructive", description: "Failed to load arena" }); }
    finally { setLoading(false); }
  };

  const loadSubmissions = useCallback(async () => {
    try {
      const qFilter = communityQ ? `&question_id=${communityQ}` : "";
      const res = await fetch(`${API_BASE}/aspirant-network/arena/submissions?sort_by=${sortBy}${qFilter}`, { headers });
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch {}
  }, [sortBy, communityQ]);

  const loadBest = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/arena/best-answers?period=${bestPeriod}`, { headers });
      setBestAnswers(await res.json());
    } catch {}
  }, [bestPeriod]);

  useEffect(() => { if (tab === "community") loadSubmissions(); }, [tab, sortBy, communityQ, loadSubmissions]);
  useEffect(() => { if (tab === "best") loadBest(); }, [tab, bestPeriod, loadBest]);

  const handleSubmit = async () => {
    if (!answer.trim() || answer.trim().length < 20) {
      toast({ variant: "destructive", description: "Write at least 20 characters" });
      return;
    }
    setSubmitting(true);
    try {
      const q = arena.questions[selectedQ];
      const res = await fetch(`${API_BASE}/aspirant-network/arena/submit`, {
        method: "POST", headers,
        body: JSON.stringify({ question_id: q.id, answer: answer.trim() }),
      });
      if (!res.ok) {
        const err = await res.json();
        toast({ variant: "destructive", description: err.detail || "Submission failed" });
        return;
      }
      const data = await res.json();
      setEvaluation(data);
      setArena(prev => ({
        ...prev,
        submitted_question_ids: {
          ...(prev.submitted_question_ids || {}),
          [q.id]: { question_id: q.id, score: data.score, marks: data.max_marks }
        }
      }));
      toast({ description: `Score: ${data.score}/${data.max_marks} — ${data.grade}` });
    } catch { toast({ variant: "destructive", description: "Submission failed" }); }
    finally { setSubmitting(false); }
  };

  const handleUpvote = async (subId) => {
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/arena/upvote/${subId}`, { method: "POST", headers });
      const data = await res.json();
      setSubmissions(prev => prev.map(s => s.submission_id === subId ? { ...s, upvotes: data.upvotes } : s));
    } catch {}
  };

  const handleBookmark = async (subId) => {
    try {
      await fetch(`${API_BASE}/aspirant-network/arena/bookmark/${subId}`, { method: "POST", headers });
      toast({ description: "Bookmark toggled" });
    } catch {}
  };

  const viewFull = async (subId) => {
    try {
      const res = await fetch(`${API_BASE}/aspirant-network/arena/submission/${subId}`, { headers });
      setViewingSub(await res.json());
    } catch {}
  };

  const wordCount = answer.trim() ? answer.trim().split(/\s+/).length : 0;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;
  }

  const currentQ = arena?.questions?.[selectedQ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/dashboard")} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl" data-testid="arena-back-btn">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2"><PenTool className="w-6 h-6" /> Daily Answer Arena</h1>
                <p className="text-indigo-200 text-sm">Write. Get evaluated. Learn from peers.</p>
              </div>
            </div>
            <div className="text-center bg-white/10 rounded-xl px-4 py-2">
              <p className="text-xs text-indigo-200">{arena?.paper_label}</p>
              <p className="text-lg font-bold">{arena?.total_submissions_today || 0}</p>
              <p className="text-[10px] text-indigo-200">writers today</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-3 mb-6" data-testid="arena-tabs">
            <TabsTrigger value="write" data-testid="arena-tab-write">Write Answer</TabsTrigger>
            <TabsTrigger value="community" data-testid="arena-tab-community">Community</TabsTrigger>
            <TabsTrigger value="best" data-testid="arena-tab-best">Best Answers</TabsTrigger>
          </TabsList>

          {/* ─── WRITE TAB ──────────────────────────────────────── */}
          <TabsContent value="write" className="space-y-4">
            {arena?.questions?.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {arena.questions.map((q, i) => {
                  const isSubmitted = arena.submitted_question_ids?.[q.id];
                  return (
                    <button key={q.id} onClick={() => { setSelectedQ(i); setAnswer(""); setEvaluation(null); }}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        i === selectedQ ? "bg-indigo-600 text-white"
                        : isSubmitted ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                        : "bg-white text-slate-600 border hover:border-indigo-300"
                      }`}
                      data-testid={`arena-q-${i}`}>
                      {isSubmitted && <CheckCircle className="w-3.5 h-3.5" />}
                      Q{i + 1}: {q.topic}
                    </button>
                  );
                })}
              </div>
            )}

            {currentQ && (
              <Card className="border-indigo-200 bg-indigo-50/50">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-indigo-100 text-indigo-700">{currentQ.paper_label}</Badge>
                    <Badge variant="outline">{currentQ.marks} marks</Badge>
                    <Badge variant="outline">{currentQ.word_limit} words</Badge>
                  </div>
                  <p className="text-lg font-medium text-slate-900 mt-2 leading-relaxed">{currentQ.question}</p>
                </CardContent>
              </Card>
            )}

            {evaluation ? (
              <Card className="border-2 border-emerald-200">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-slate-900">Your Evaluation</h3>
                    <div className="text-center">
                      <p className={`text-4xl font-bold ${evaluation.score >= evaluation.max_marks * 0.7 ? 'text-emerald-600' : evaluation.score >= evaluation.max_marks * 0.5 ? 'text-amber-600' : 'text-red-600'}`}>
                        {evaluation.score}/{evaluation.max_marks}
                      </p>
                      <Badge className={`mt-1 ${evaluation.grade === 'A' ? 'bg-emerald-100 text-emerald-700' : evaluation.grade === 'B' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        Grade {evaluation.grade}
                      </Badge>
                    </div>
                  </div>

                  {evaluation.evaluation?.overall_feedback && (
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-xl">{evaluation.evaluation.overall_feedback}</p>
                  )}

                  {/* Rubric breakdown */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {["introduction", "content", "structure", "dimensions", "examples", "conclusion", "language"].map(key => {
                      const param = evaluation.evaluation?.[key];
                      if (!param) return null;
                      return (
                        <div key={key} className="bg-slate-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-slate-800">{param.score}</p>
                          <p className="text-xs text-slate-500 capitalize">{key}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid md:grid-cols-2 gap-3">
                    {evaluation.evaluation?.strengths?.length > 0 && (
                      <div className="bg-emerald-50 rounded-xl p-3">
                        <p className="text-xs font-bold text-emerald-700 mb-1">Strengths</p>
                        {evaluation.evaluation.strengths.map((s, i) => (
                          <p key={i} className="text-sm text-slate-700 flex items-start gap-1"><CheckCircle className="w-3 h-3 text-emerald-500 mt-1 shrink-0" />{s}</p>
                        ))}
                      </div>
                    )}
                    {evaluation.evaluation?.improvements?.length > 0 && (
                      <div className="bg-amber-50 rounded-xl p-3">
                        <p className="text-xs font-bold text-amber-700 mb-1">Improve</p>
                        {evaluation.evaluation.improvements.map((s, i) => (
                          <p key={i} className="text-sm text-slate-700 flex items-start gap-1"><TrendingUp className="w-3 h-3 text-amber-500 mt-1 shrink-0" />{s}</p>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button onClick={() => setTab("community")} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="view-community-btn">
                    <Users className="w-4 h-4 mr-2" /> See How Others Answered
                  </Button>
                </CardContent>
              </Card>
            ) : (currentQ && arena?.submitted_question_ids?.[currentQ.id]) ? (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-slate-900">Submitted for this question!</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Score: <strong>{arena.submitted_question_ids[currentQ.id]?.score}/{arena.submitted_question_ids[currentQ.id]?.marks}</strong>
                    {arena.questions.length > 1 && " — Try the other questions above"}
                  </p>
                  <div className="flex gap-2 justify-center mt-4">
                    <Button onClick={() => { setCommunityQ(currentQ.id); setTab("community"); }} data-testid="go-community-btn">
                      View Community Answers
                    </Button>
                    {arena.questions.some((q) => !arena.submitted_question_ids?.[q.id]) && (
                      <Button variant="outline" onClick={() => {
                        const nextIdx = arena.questions.findIndex((q) => !arena.submitted_question_ids?.[q.id]);
                        if (nextIdx >= 0) { setSelectedQ(nextIdx); setAnswer(""); setEvaluation(null); }
                      }} data-testid="next-question-btn">
                        Next Unanswered Question
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Start writing your answer here..."
                    className="w-full min-h-[300px] p-6 text-base leading-relaxed resize-none focus:outline-none rounded-xl"
                    data-testid="arena-answer-input"
                  />
                  <div className="px-6 pb-4 flex items-center justify-between border-t border-slate-100 pt-3">
                    <span className="text-sm text-slate-500">
                      {wordCount} words {currentQ && <span className="text-slate-400">/ {currentQ.word_limit} target</span>}
                    </span>
                    <Button onClick={handleSubmit} disabled={submitting || wordCount < 10}
                      className="bg-indigo-600 hover:bg-indigo-700" data-testid="arena-submit-btn">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Submit & Evaluate
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ─── COMMUNITY TAB ──────────────────────────────────── */}
          <TabsContent value="community" className="space-y-4">
            {/* Question Filter */}
            {arena?.questions?.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setCommunityQ(null)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium ${!communityQ ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border hover:border-indigo-300'}`}
                  data-testid="community-filter-all">All Questions</button>
                {arena.questions.map((q, i) => (
                  <button key={q.id} onClick={() => setCommunityQ(q.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium ${communityQ === q.id ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border hover:border-indigo-300'}`}
                    data-testid={`community-filter-q${i}`}>
                    Q{i + 1}: {q.topic}
                  </button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900">
                {communityQ ? `Submissions for Q${(arena?.questions?.findIndex(q => q.id === communityQ) ?? 0) + 1}` : "All Submissions Today"}
              </h3>
              <div className="flex gap-2">
                {["score", "upvotes"].map(s => (
                  <button key={s} onClick={() => setSortBy(s)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${sortBy === s ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border'}`}>
                    Sort by {s === "score" ? "Score" : "Votes"}
                  </button>
                ))}
              </div>
            </div>

            {submissions.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-slate-500">No submissions yet today. Be the first!</CardContent></Card>
            ) : submissions.map((sub, idx) => (
              <Card key={sub.submission_id} className={`hover:shadow-md transition-all ${idx === 0 ? 'border-amber-300 bg-amber-50/30' : ''}`}
                data-testid={`community-sub-${idx}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {idx === 0 && <Crown className="w-5 h-5 text-amber-500" />}
                      <div>
                        <p className="font-semibold text-slate-900">{sub.user_name}</p>
                        <p className="text-xs text-slate-500">
                          {!communityQ && arena?.questions?.length > 1 && <span className="text-indigo-600 font-medium">Q{(arena?.questions?.findIndex(q => q.id === sub.question_id) ?? 0) + 1} · </span>}
                          {sub.word_count} words · {sub.topic}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${sub.score >= sub.marks * 0.7 ? 'text-emerald-600' : sub.score >= sub.marks * 0.5 ? 'text-amber-600' : 'text-red-600'}`}>
                        {sub.score}/{sub.marks}
                      </p>
                      <Badge variant="outline" className="text-xs">{sub.ai_evaluation?.grade || 'N/A'}</Badge>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
                    <button onClick={() => handleUpvote(sub.submission_id)}
                      className="flex items-center gap-1 text-sm text-slate-600 hover:text-indigo-600"
                      data-testid={`upvote-${sub.submission_id}`}>
                      <ThumbsUp className="w-4 h-4" /> {sub.upvotes || 0}
                    </button>
                    <button onClick={() => handleBookmark(sub.submission_id)}
                      className="flex items-center gap-1 text-sm text-slate-600 hover:text-amber-600">
                      <Bookmark className="w-4 h-4" />
                    </button>
                    <span className="flex items-center gap-1 text-sm text-slate-400">
                      <MessageCircle className="w-4 h-4" /> {sub.comments?.length || 0}
                    </span>
                    <button onClick={() => viewFull(sub.submission_id)}
                      className="ml-auto flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      data-testid={`view-full-${sub.submission_id}`}>
                      <Eye className="w-4 h-4" /> Read Full
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Full answer modal */}
            {viewingSub && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewingSub(null)}>
                <Card className="max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-900">{viewingSub.user_name}</p>
                        <p className="text-xs text-slate-500">{viewingSub.word_count} words</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-indigo-600">{viewingSub.score}/{viewingSub.marks}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-slate-600 mb-2">Question:</p>
                      <p className="text-sm text-slate-800">{viewingSub.question}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-2">Answer:</p>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{viewingSub.answer}</p>
                    </div>
                    {viewingSub.ai_evaluation?.overall_feedback && (
                      <div className="bg-indigo-50 rounded-xl p-4">
                        <p className="text-sm font-medium text-indigo-700 mb-1">AI Feedback:</p>
                        <p className="text-sm text-slate-700">{viewingSub.ai_evaluation.overall_feedback}</p>
                      </div>
                    )}
                    <Button variant="outline" onClick={() => setViewingSub(null)} className="w-full">Close</Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* ─── BEST ANSWERS TAB ───────────────────────────────── */}
          <TabsContent value="best" className="space-y-4">
            <div className="flex gap-2">
              {[
                { key: "today", label: "Today" },
                { key: "this_week", label: "This Week" },
                { key: "this_month", label: "This Month" },
              ].map(p => (
                <button key={p.key} onClick={() => setBestPeriod(p.key)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium ${bestPeriod === p.key ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border hover:border-indigo-300'}`}
                  data-testid={`best-period-${p.key}`}>
                  {p.label}
                </button>
              ))}
            </div>

            {bestAnswers?.top_writers?.length > 0 && (
              <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                <CardContent className="p-5">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Top Writers</h3>
                  <div className="space-y-2">
                    {bestAnswers.top_writers.map((w, i) => (
                      <div key={w.user_id} className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-slate-300 text-slate-700' : i === 2 ? 'bg-orange-400 text-white' : 'bg-slate-100 text-slate-600'}`}>
                            {i + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm text-slate-900">{w.name}</p>
                            <p className="text-xs text-slate-500">{w.submissions} answers · {w.upvotes} votes</p>
                          </div>
                        </div>
                        <p className="font-bold text-indigo-600">{w.avg_score}/15</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {bestAnswers?.best_answers?.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-bold text-slate-900 flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> Highest Scoring Answers</h3>
                {bestAnswers.best_answers.map((ans, i) => (
                  <Card key={ans.submission_id} className="hover:shadow-md">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-slate-900">{ans.user_name}</p>
                          <p className="text-xs text-slate-500">{ans.topic} · {ans.word_count} words</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-emerald-600">{ans.score}/{ans.marks}</p>
                          <p className="text-xs text-slate-500">{ans.upvotes} votes</p>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => viewFull(ans.submission_id)} className="mt-2"
                        data-testid={`read-best-${i}`}>
                        <Eye className="w-3 h-3 mr-1" /> Read Answer
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {(!bestAnswers?.best_answers?.length && !bestAnswers?.top_writers?.length) && (
              <Card><CardContent className="p-8 text-center text-slate-500">No submissions yet for this period. Start writing!</CardContent></Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AnswerArenaPage;
